<script setup lang="ts">
import {
  computed, onMounted, watch,
} from 'vue';
import { useAppConfig } from '@/shared/composables/useAppConfig';
import { useRoute } from 'vue-router';
import { App } from '@capacitor/app';

import EmptyLayout from '@/shared/layouts/EmptyLayout.vue';
import MainLayout from '@/shared/layouts/MainLayout.vue';
import AdminSidebarLayout from '@/modules/admin/layouts/AdminSidebarLayout.vue';
import AdminPlainLayout from '@/modules/admin/layouts/AdminPlainLayout.vue';

import NoInternetScreen from '@/shared/components/core/NoInternetScreen.vue';
import AppUpdateModal from '@/shared/components/core/AppUpdateModal.vue';

import { Capacitor } from '@capacitor/core';
import { useLogAppEventAnaliytics } from './shared/composables/useLogAppEventAnaliytics';
import { useSectionExitTracker } from '@/core/analytics/useSectionExitTracker';
import { useAppUpdate } from '@/shared/composables/useAppUpdate';
import { useLoaderAtStartStore } from '@/shared/components/loaders/LoaderAtStartApp/store';
import { storeToRefs } from 'pinia';
import { logAnalyticsEvent } from '@/core/analytics/analytics';
import { AnalyticsEventName, SectionEntrySourceNames } from '@/core/analytics/types';
import { getLastActiveAppSection } from '@/shared/tools/get-redirect-route';
import { handleAppOpenUrl } from '@/core/plugins/capacitor/deeplinks';
import { AppScreens } from '@/shared/types';
import { useMainStore } from './core/store/main';

// const swReady = ref<boolean>(false);

onMounted(async () => {
  window.addEventListener('resize', appHeight);

  appHeight();

  // =========== Share link handler ========== Start

  // коли користувач на мобільному пристрої відкриває посилання,
  // до якого прив'язано наш додаток, і якщо на пристрої вже
  // встановлено наш додаток, то ми перевіряємо, чи відкрите
  // посилання є Shared link (тобто посилання, що було сформоване
  // натискання на кнопку "Поділитися" на слайді в нашому додатку),
  // і, якщо це Shared link, то ми зберігаємо на api інформацію про це
  // відкриття посилання.

  if (Capacitor.isNativePlatform()) {
    // Перевірка, чи був додаток запущений через посилання (cold start)
    App.getLaunchUrl().then((launchUrl) => {
      if (launchUrl) {
        handleAppOpenUrl(launchUrl.url);
      } else {
        const lastActiveAppSection = getLastActiveAppSection();

        logAnalyticsEvent({
          name: AnalyticsEventName.AppBackground,
          params: {
            appSection: lastActiveAppSection,
            source: SectionEntrySourceNames.Organic,
          },
        });
      }
    });
  }

  // =========== Share link handler ========== End
});
//
// console.log('import.meta.env.VITE_APP_ENV', import.meta.env.VITE_APP_ENV);
// if (import.meta.env.VITE_APP_ENV !== 'development') {
//   navigator.serviceWorker.ready.then(() => {
//     if (!navigator.serviceWorker.controller) {
//       console.log('There is no sw controller. Reload');
//       window.location.reload();
//     } else {
//       console.log('There is controller', navigator.serviceWorker.controller);
//       swReady.value = true;
//     }
//   });
// } else {
//   swReady.value = true;
// }

const appHeight = () => {
  const root = document.getElementById('app');
};

appHeight();

const {
  progressBarHeight,
  contentContainerTopPadding,
  contentContainerBottomPadding,
  contentContainerRightPadding,
  contentContainerLeftPaddingLarge,
  contentContainerLeftPaddingSmall,
  fontFamily,
  theme,
} = useAppConfig();

const layouts = {
  'empty': EmptyLayout,
  'main': MainLayout,
  'admin': AdminPlainLayout,
  'admin-sidebar': AdminSidebarLayout,
};

const route = useRoute();
const layoutName = computed((): (keyof typeof layouts) => route.meta.layout as (keyof typeof layouts) || 'empty');
const layout = computed(() => layoutName.value ? layouts[layoutName.value] : null);

useLogAppEventAnaliytics();
useSectionExitTracker();

const {
  showUpdateModal,
  availableVersion,
  performUpdate,
  dismissUpdate,
  checkForUpdate,
} = useAppUpdate();

// Trigger update check after startup loader finishes
const { visible: loaderVisible } = storeToRefs(useLoaderAtStartStore());
watch(loaderVisible, (isVisible) => {
  if (!isVisible) {
    checkForUpdate();
  }
});

const NoInternetScreenDesabledSections = [AppScreens.TextBible];
const { activeScreen } = storeToRefs(useMainStore());

</script>

<template>
  <component
    :is="layout"
    v-if="layout"
  />

  <NoInternetScreen
    v-if="!NoInternetScreenDesabledSections.includes(activeScreen)"
    class="no-internet-component"
  />

  <AppUpdateModal
    :visible="showUpdateModal"
    :version="availableVersion"
    @update="performUpdate"
    @dismiss="dismissUpdate"
  />
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap');
</style>

<style>
* {
  --progressBarHeight: v-bind(progressBarHeight + 'px');
  --contentContainerTopPadding: v-bind(contentContainerTopPadding + 'px');
  --contentContainerBottomPadding: v-bind(contentContainerBottomPadding + 'px');
  --contentContainerRightPadding: v-bind(contentContainerRightPadding + 'px');
  --contentContainerLeftPaddingLarge: v-bind(contentContainerLeftPaddingLarge + 'px');
  --contentContainerLeftPaddingSmall: calc(1px * v-bind(contentContainerLeftPaddingSmall));
  --fontFamily: v-bind(fontFamily);

  --theme-dark-defaultTextColor: v-bind('theme.dark.defaultTextColor');
  --theme-light-defaultTextColor: v-bind('theme.light.defaultTextColor');
}

.app-loader {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 500;
}

.no-internet-component {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 550;
}

@media all and (min-width: 601px) {
  .no-internet-component {
    zoom: var(--zoom-for-desktop);
  }
}
</style>
